/**
 * /venues/:venueSlug/scorer — the tablet beside the board.
 *
 * Reached only by pairing. It reads the session /board wrote — a token and the
 * ids needed to open a socket — and sends the marker back there if it finds
 * none. There is no link to this page anywhere in the app, because a link
 * without a token is a dead end (see routes.jsx).
 *
 * WHAT IS ON SCREEN = WHAT THE ROOM SAID + WHAT HAS NOT BEEN SENT YET. The
 * room's board is replaced wholesale on every sync and never patched;
 * projectMatch replays the outstanding queue over it. So a reconnect mid-leg
 * cannot make the display jump backwards, and nothing in storage is ever read
 * as game state — which is what stops a dropped tablet coming back looking
 * like a fresh match (V2-CARRYOVER.md §11).
 *
 * TURN ORDER IS OFFERED, NOT ENFORCED. The player whose throw it is comes up
 * selected; the marker can score for either. V1 insisted, and operators
 * answered by typing fake busts to shove the turn along — those visits are
 * still in the V1 data (V2-CARRYOVER.md §2). The room does not enforce it
 * either; this matches.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { readBoardSession, BOARD_SESSION_KEY } from './Board.jsx';
import { useScorerRoom } from '../lib/scorerRoom.js';
import { enqueue } from '../lib/scorerQueue.js';
import { projectMatch, remainingAfter, outcomeOf, STARTING_SCORE } from '../lib/legMath.js';

import AssistKeypad  from '../components/scorer/AssistKeypad.jsx';
import DartEntry     from '../components/scorer/DartEntry.jsx';
import CheckoutModal from '../components/scorer/CheckoutModal.jsx';
import OfflineBanner from '../components/scorer/OfflineBanner.jsx';

/**
 * Touch, not width. A 12" tablet in landscape is wider than a small laptop, so
 * a max-width test hides the keypad exactly where it is needed and shows it
 * where it is not. Pointer type is the question actually being asked.
 */
const COARSE_QUERY = '(pointer: coarse)';

function useIsTablet() {
  const [coarse, setCoarse] = useState(() => window.matchMedia(COARSE_QUERY).matches);

  useEffect(() => {
    const mql = window.matchMedia(COARSE_QUERY);
    const onChange = (event) => setCoarse(event.matches);

    setCoarse(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return coarse;
}

export default function Scorer() {
  const navigate = useNavigate();
  const [session] = useState(readBoardSession);

  const { status, board, paired, queue, send } = useScorerRoom(session);
  const isTablet = useIsTablet();

  const [mode, setMode]   = useState('assist');
  const [entry, setEntry] = useState('');
  const [darts, setDarts] = useState([]);
  const [suit, setSuit]   = useState('S');
  const [selected, setSelected] = useState(null);
  const [checkout, setCheckout] = useState(null);

  // No session, or the room says this token no longer holds the board — both
  // mean the same thing to the marker: this tablet is not a scorer any more.
  useEffect(() => {
    if (!session) navigate('/board', { replace: true });
  }, [session, navigate]);

  useEffect(() => {
    if (!paired) {
      // The token is spent — taken over, or aged out. Clearing it is what
      // stops /board bouncing straight back here on the redirect.
      localStorage.removeItem(BOARD_SESSION_KEY);
      navigate('/board', { replace: true });
    }
  }, [paired, navigate]);

  const match = useMemo(
    () => projectMatch(board?.match ?? null, queue),
    [board, queue]
  );

  const leg = match?.currentLeg ?? null;
  const offline = status !== 'connected';

  // Whose throw it is, from the projection rather than the room's copy, so an
  // unsent visit has already moved the turn on.
  //
  // NOT throwingEntryId from tournamentRoom.js, close as it is: that one keys
  // off match.currentLegId and requires status 'live', and the leg being
  // thrown into here may exist only in the projection with no id and no row
  // behind it yet. Same rule — count the visits each has thrown and compare —
  // applied to a leg the server has not seen.
  const derived = useMemo(() => {
    if (!match || !leg) return null;

    const starter = leg.starting_player_id;
    if (starter == null) return match.player1_id;

    const other  = starter === match.player1_id ? match.player2_id : match.player1_id;
    const thrown = (id) => leg.visits.filter((v) => v.player_id === id).length;

    return thrown(starter) === thrown(other) ? starter : other;
  }, [match, leg]);

  const active = selected ?? derived;

  // A new leg clears the marker's override — it is an override of one turn,
  // not a preference for the rest of the match.
  useEffect(() => { setSelected(null); }, [leg?.leg_number]);

  if (!session) return null;

  const { tournamentId, boardNumber } = session;

  const remaining = (playerId) =>
    leg ? remainingAfter(leg.visits, playerId) : STARTING_SCORE;

  const nameOf = (playerId) =>
    playerId === match?.player1_id ? match?.player1_name : match?.player2_name;

  const resetEntry = () => { setEntry(''); setDarts([]); setSuit('S'); };

  /** The one place a scoring event is created. Everything else routes here. */
  const record = (score, dartsUsed, isCheckoutVisit) => {
    const before = remaining(active);
    const result = outcomeOf(before, score);

    enqueue(tournamentId, boardNumber, 'submit_visit', {
      matchId:    match.id,
      // Both, and the room prefers the id. An offline leg has no id yet, and a
      // queued visit can carry one that was never real on this server — the
      // number is what survives either way.
      legId:      leg?.id ?? null,
      legNumber:  leg?.leg_number,
      playerId:   active,
      // The score AS THROWN, even on a bust. The room stores the bust flag and
      // zeroes the score itself; sending zero would destroy the only record of
      // what was actually hit (V2-CARRYOVER.md §3).
      score,
      dartsUsed,
      isBust:     result === 'bust',
      isCheckout: Boolean(isCheckoutVisit) || result === 'checkout',
      thrownAt:   new Date().toISOString(),
    });

    resetEntry();
    setSelected(null);
  };

  /** Assist: a finish has to stop and ask how many darts it took. */
  const submitAssist = (score) => {
    if (outcomeOf(remaining(active), score) === 'checkout') {
      setCheckout({ score, darts: 3 });
      return;
    }
    record(score, 3, false);
  };

  /** Manual: the slots already say how many darts, so a finish just goes. */
  const submitManual = (total, dartCount) => record(total, dartCount, false);

  const startMatch = (startingPlayerId) => {
    // NOT QUEUED, deliberately. Starting a match offline would have the tablet
    // inventing leg one and a bull winner the room has never heard of, and a
    // board waiting on its next match is a board nobody is throwing at yet —
    // so the honest thing is to stall and say so. Finishing a match offline is
    // the case that matters, and that works.
    send({ type: 'start_match', matchId: board.upcoming[0].id, startingPlayerId });
  };

  // ---- Not paired to anything yet ----------------------------------------
  if (!board) {
    return (
      <div className="hd-fullscreen hd-scorer">
        {offline && <OfflineBanner variant="waiting" />}
        <div className="hd-scorer-idle">Connecting to Board {session.boardNumber}…</div>
      </div>
    );
  }

  // ---- Nothing live: waiting for the next match --------------------------
  if (!match) {
    const next = board.upcoming?.[0] ?? null;

    return (
      <div className="hd-fullscreen hd-scorer">
        {offline && <OfflineBanner variant="waiting" />}

        <div className="hd-scorer-idle">
          <div className="hd-scorer-board">Board {board.boardNumber}</div>

          {next ? (
            <>
              <h1 className="hd-scorer-next">
                {next.player1_name ?? 'TBC'} v {next.player2_name ?? 'TBC'}
              </h1>
              <p className="hd-meta">Marker: {next.marker_name ?? '—'}</p>
              <p className="hd-meta">Who won the bull?</p>

              <div className="hd-scorer-bull">
                {[next.player1_id, next.player2_id].map((id, i) => (
                  <button
                    key={id ?? i}
                    type="button"
                    className="hd-btn hd-btn-primary hd-scorer-bull-btn"
                    disabled={offline || id == null}
                    onClick={() => startMatch(id)}
                  >
                    {i === 0 ? next.player1_name : next.player2_name}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="hd-meta">Nothing queued for this board.</p>
          )}
        </div>
      </div>
    );
  }

  // ---- Scoring -----------------------------------------------------------
  return (
    <div className="hd-fullscreen hd-scorer">
      {offline && <OfflineBanner variant="scoring" />}

      <div className="hd-scorer-head">
        <span className="hd-scorer-board">Board {board.boardNumber}</span>
        <span className="hd-meta">
          Leg {leg?.leg_number ?? '—'} · first to {match.legsToWin}
        </span>
        <button
          type="button"
          className="hd-btn hd-btn-secondary hd-btn-sm"
          onClick={() => setMode(mode === 'assist' ? 'manual' : 'assist')}
        >
          {mode === 'assist' ? 'Assist' : 'Manual'}
        </button>
      </div>

      <div className="hd-scorer-players">
        {[match.player1_id, match.player2_id].map((id) => (
          <button
            key={id}
            type="button"
            className={`hd-scorer-player ${active === id ? 'hd-scorer-active' : ''}`}
            aria-pressed={active === id}
            onClick={() => setSelected(id)}
          >
            <span className="hd-scorer-name">{nameOf(id)}</span>
            <span className="hd-scorer-remaining">{remaining(id)}</span>
            <span className="hd-scorer-legs">{match.legsWon[id] ?? 0}</span>
          </button>
        ))}
      </div>

      {isTablet ? (
        mode === 'assist' ? (
          <AssistKeypad
            entry={entry}
            onEntry={setEntry}
            onSubmit={submitAssist}
            disabled={!leg}
          />
        ) : (
          <DartEntry
            darts={darts}
            suit={suit}
            onSuit={setSuit}
            onDart={(dart) => setDarts([...darts, dart])}
            onUndo={() => setDarts(darts.slice(0, -1))}
            onSubmit={submitManual}
            disabled={!leg}
          />
        )
      ) : (
        /* Desktop keeps a working, compact entry rather than nothing: the page
           has to be testable without a tablet on the desk. */
        <form
          className="hd-scorer-desk"
          onSubmit={(event) => {
            event.preventDefault();
            if (entry !== '') submitAssist(Number(entry));
          }}
        >
          <label className="hd-label" htmlFor="hd-desk-score">
            Visit total for {nameOf(active)}
          </label>
          <input
            id="hd-desk-score"
            className="hd-input"
            inputMode="numeric"
            value={entry}
            onChange={(e) => setEntry(e.target.value.replace(/\D/g, '').slice(0, 3))}
          />
          <button type="submit" className="hd-btn hd-btn-primary" disabled={entry === ''}>
            Enter
          </button>
          <p className="hd-meta">The full keypad appears on a touch screen.</p>
        </form>
      )}

      {checkout && (
        <CheckoutModal
          playerName={nameOf(active)}
          score={checkout.score}
          darts={checkout.darts}
          onDarts={(n) => setCheckout({ ...checkout, darts: n })}
          onConfirm={() => {
            record(checkout.score, checkout.darts, true);
            setCheckout(null);
          }}
          onCancel={() => setCheckout(null)}
        />
      )}
    </div>
  );
}
